import React, { useState, useEffect, useRef } from 'react';

const SCALE_LABELS = ['','Strongly Disagree','Disagree','Neutral / Inconsistent','Agree','Strongly Agree'];
const SCALE_COLORS = ['','var(--red)','var(--orange)','var(--yellow)','var(--green)','var(--cyan)'];
const DEPTS = ['Sales','Operations','HR','Finance','Marketing','Technology','Product','R&D','Customer Success','General Management','Other'];
const LEVELS = [{v:'Leadership',l:'Leadership (C-Suite / VP / Director)'},{v:'Manager',l:'Manager / Senior Manager'},{v:'Executive',l:'Individual Contributor / Executive'}];

const INDUSTRY_FOCUS = {
  'Technology':           'Focus: Strategy, Execution, Technology, HR & Culture',
  'Manufacturing':        'Focus: Process, Operations, Accountability, Risk',
  'Financial Services':   'Focus: Risk, Financial Health, Compliance, Strategy',
  'Healthcare':           'Focus: Risk, Process, HR & Culture, Customer Experience',
  'Retail':               'Focus: Customer Experience, Sales, Operations, Technology',
  'Professional Services':'Focus: HR & Culture, Sales, Strategy, Customer Experience',
  'Education':            'Focus: HR & Culture, Strategy, Customer Experience, Process',
};

export default function PublicAssess({ companyId, showToast }) {
  const [info, setInfo] = useState(null);
  const [step, setStep] = useState(0);
  const topRef = useRef(null);
  const [meta, setMeta] = useState({ respondent_name:'', respondent_email:'', department:'', level:'' });
  const STORAGE_KEY = `orgdna_assess_${companyId}`;

  // ── Restore saved progress from localStorage ──────────────────────────
  const getSaved = () => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
  };
  const saved = getSaved();

  const [answers, setAnswers] = useState(saved?.answers || {});
  const [openAnswers, setOpenAnswers] = useState(saved?.openAnswers || ['','','','']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedDims, setSelectedDims] = useState([]);
  const [savedBanner, setSavedBanner] = useState(false);  // "Progress restored" banner


  // ── Auto-save answers to localStorage whenever they change ─────────────
  useEffect(() => {
    if (Object.keys(answers).length === 0 && openAnswers.every(a => !a)) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        answers, openAnswers, step, meta, savedAt: new Date().toISOString()
      }));
    } catch (e) { /* quota exceeded — ignore */ }
  }, [answers, openAnswers, step, meta]);

  // Show "progress restored" banner if saved data was found
  useEffect(() => {
    if (saved && Object.keys(saved.answers || {}).length > 0) {
      setSavedBanner(true);
      setTimeout(() => setSavedBanner(false), 5000);
    }
  }, []);

  const scrollToTop = () => {
    // The root div IS the scroll container (overflowY:auto, height:100vh)
    // so we set its scrollTop to 0, not scrollIntoView
    const el = topRef.current || document.getElementById('assess-scroll-root');
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    fetch(`/api/assessments/public/${companyId}/info`)
      .then(r => r.json()).then(d => {
        if (d.company) {
          setInfo(d);
          const dimOrder = d.dimOrder || Object.keys(d.dimensions || {});
          setSelectedDims(dimOrder.slice(0, 12));
          // Restore step from saved progress (after dims are loaded)
          if (saved?.step && saved.step > 0) {
            setStep(saved.step);
            setMeta(saved.meta || { respondent_name:'', respondent_email:'', department:'', level:'' });
          }
        } else setError(d.error || 'Assessment not found. Check the URL.');
      }).catch(() => setError('Could not load assessment. Please check the URL.'));
  }, [companyId]);

  const dims = info && selectedDims.length > 0
    ? selectedDims.map(k => [k, info.dimensions[k]]).filter(([, v]) => v)
    : [];

  const totalSections = 1 + dims.length + 1;
  const progress = Math.round((step / totalSections) * 100);

  const getQsForDim = (dimKey, dimData) => {
    const dimIdx = (info.dimOrder || []).indexOf(dimKey);
    const base = dimIdx >= 0 ? dimIdx * 10 : 0;
    return dimData.questions.map((q, i) => ({ text: q, key: `q${base + i}` }));
  };

  const canProceed = () => {
    if (step === 0) return meta.respondent_name.trim() && meta.level;
    if (step >= 1 && step <= dims.length) {
      const [dimKey, dimData] = dims[step - 1];
      return getQsForDim(dimKey, dimData).every(q => answers[q.key]);
    }
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`/api/assessments/public/${companyId}/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...meta, answers, open_ended: openAnswers, round_id: info.round?.id })
      });
      const data = await res.json();
      if (res.ok) {
        setStep(totalSections);
        try { localStorage.removeItem(STORAGE_KEY); } catch {}
      }
      else setError(data.error || 'Submission failed. Please try again.');
    } catch { setError('Submission failed. Check your connection.'); }
    setSubmitting(false);
  };

  if (error) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:24, background:'var(--bg)' }}>
      <div style={{ textAlign:'center', maxWidth:400 }}>
        <div style={{ fontSize:48, marginBottom:16 }}>⚠️</div>
        <div style={{ color:'var(--red)', fontSize:16, marginBottom:12 }}>{error}</div>
        <div style={{ fontSize:12, color:'var(--text3)' }}>If this link was shared with you, ask the sender to verify it.</div>
      </div>
    </div>
  );

  if (!info) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)' }}>
      <div className="loader"></div>
    </div>
  );

  // Thank you screen
  if (step === totalSections) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:24, background:'var(--bg)' }}>
      <div style={{ textAlign:'center', maxWidth:520, background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:48 }} className="fade-in">
        <div style={{ fontSize:56, marginBottom:20 }}>✅</div>
        <div style={{ fontFamily:'var(--ff-d)', fontSize:24, fontWeight:800, marginBottom:12 }}>Thank you!</div>
        <div style={{ color:'var(--text2)', fontSize:15, lineHeight:1.6, marginBottom:20 }}>
          Your assessment for <strong style={{ color: info.company.whitelabel_name || info.company.name ? 'var(--accent)' : 'var(--text)' }}>
            {info.company.whitelabel_name || info.company.name}
          </strong> has been submitted successfully.
        </div>
        <div style={{ padding:'12px 16px', background:'rgba(201,151,58,0.05)', borderRadius:8, border:'1px solid rgba(0,212,255,0.15)', fontSize:13, color:'var(--text2)' }}>
          Your honest responses contribute to a more accurate organizational diagnosis. Results are aggregated — individual answers are never shown.
        </div>
        <div style={{ fontSize:12, color:'var(--text3)', marginTop:20 }}>You may close this tab.</div>
      </div>
    </div>
  );

  const accentColor = info.company.whitelabel_color || '#2563EB';
  const brandName = info.company.whitelabel_name || info.company.name;
  const industryFocus = INDUSTRY_FOCUS[info.company.industry] || '';
  const currentDim = step >= 1 && step <= dims.length ? dims[step - 1] : null;
  const isOpenEnded = step === dims.length + 1;
  const dimsDone = step - 1;

  return (
    <div id="assess-scroll-root" ref={topRef} style={{ minHeight:'100vh', background:'var(--bg)', padding:'clamp(16px,4vw,32px) clamp(12px,4vw,20px)', overflowY:'auto', height:'100vh', boxSizing:'border-box' }}>
      <div style={{ maxWidth:700, margin:'0 auto', width:'100%' }}>
        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 14px', background:`${accentColor}18`, border:`1px solid ${accentColor}40`, borderRadius:20, marginBottom:12 }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:accentColor }}></div>
            <span style={{ fontFamily:'var(--ff-m)', fontSize:11, color:accentColor, letterSpacing:1, textTransform:'uppercase' }}>Org DNA Assessment</span>
          </div>
          <div style={{ fontFamily:'var(--ff-d)', fontSize:22, fontWeight:800, marginBottom:4 }}>{brandName}</div>
          {info.round && <div style={{ color:'var(--text3)', fontSize:11, fontFamily:'var(--ff-m)' }}>{info.round.label}</div>}
          {info.company.anonymous_mode === 1 && (
            <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 12px', background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:20, marginTop:8 }}>
              <span style={{ fontSize:11, color:'var(--green)' }}>🔒 Anonymous mode — your name is not collected</span>
            </div>
          )}
        </div>

        {/* Saved progress banner */}
        {savedBanner && (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', background:'rgba(22,163,74,0.1)', border:'1px solid rgba(22,163,74,0.25)', borderRadius:10, marginBottom:16, fontSize:13, color:'var(--green)' }}>
            <span>✅</span>
            <span><strong>Progress restored.</strong> We saved your previous answers — continue where you left off.</span>
            <button onClick={() => { setAnswers({}); setOpenAnswers(['','','','']); setStep(0); setMeta({ respondent_name:'', respondent_email:'', department:'', level:'' }); try { localStorage.removeItem(STORAGE_KEY); } catch {} setSavedBanner(false); }}
              style={{ marginLeft:'auto', fontSize:11, color:'var(--text-3)', background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>
              Start fresh
            </button>
          </div>
        )}

        {/* Progress */}
        <div className="progress-wrap"><div className="progress-fill" style={{ width:`${progress}%`, background:`linear-gradient(90deg, ${accentColor}, #7c3aed)` }}></div></div>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'var(--text3)', fontFamily:'var(--ff-m)', marginBottom:20, marginTop:3 }}>
          <span>Step {step + 1} of {totalSections}</span>
          <span>{progress}% complete</span>
        </div>

        {/* Dim progress dots */}
        <div style={{ display:'flex', gap:3, marginBottom:20, flexWrap:'wrap' }}>
          {dims.map(([k, d], i) => (
            <div key={k} title={d.label} style={{ flex:1, minWidth:6, height:4, borderRadius:2, background: i < dimsDone ? 'var(--green)' : i === dimsDone && step > 0 ? accentColor : 'var(--bg3)', transition:'all 0.3s', cursor:'default' }}></div>
          ))}
        </div>

        {/* Section header */}
        <div className="card card-accent" style={{ marginBottom:20, padding:'16px 20px', borderColor:`${accentColor}30` }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
            <span className="tag">{step === 0 ? 'Your Details' : isOpenEnded ? 'Open Questions' : `${dimsDone + 1} of ${dims.length}`}</span>
            {currentDim && <span className="tag" style={{ background:`var(--blue-soft)`, color:'var(--blue)', borderColor:'rgba(201,151,58,0.2)' }}>{currentDim[1].code}</span>}
            {step === 0 && info.company.industry && industryFocus && <span className="tag" style={{ fontSize:10 }}>{info.company.industry}</span>}
          </div>
          <div style={{ fontFamily:'var(--ff-d)', fontSize:17, fontWeight:800, marginBottom:3 }}>
            {step === 0 ? 'Your Details' : isOpenEnded ? 'Open-Ended Questions' : currentDim?.[1]?.label}
          </div>
          <div style={{ color:'var(--text2)', fontSize:12 }}>
            {step === 0
              ? (industryFocus ? `${industryFocus} · Your responses are aggregated and anonymous.` : 'Your responses are confidential and aggregated with others.')
              : isOpenEnded ? 'Optional but extremely valuable. Be specific — vague answers produce weak insights.'
              : currentDim?.[1]?.description || 'Rate each statement based on your real day-to-day experience.'}
          </div>
        </div>

        {/* STEP 0: Meta */}
        {step === 0 && (
          <div className="card">
            <div className="g2" style={{ gap:14 }}>
              {info.company.anonymous_mode !== 1 && (
                <div className="form-group">
                  <label className="form-label">Your Name *</label>
                  <input className="form-input" placeholder="Full name" value={meta.respondent_name} onChange={e => setMeta({ ...meta, respondent_name: e.target.value })} />
                </div>
              )}
              {info.company.anonymous_mode === 1 && (
                <div className="form-group">
                  <label className="form-label">Name (optional — not stored)</label>
                  <input className="form-input" placeholder="You may leave this blank" value={meta.respondent_name} onChange={e => setMeta({ ...meta, respondent_name: e.target.value })} />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Email (optional)</label>
                <input className="form-input" type="email" placeholder="you@company.com" value={meta.respondent_email} onChange={e => setMeta({ ...meta, respondent_email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Department</label>
                <select className="form-select" value={meta.department} onChange={e => setMeta({ ...meta, department: e.target.value })}>
                  <option value="">Select department</option>
                  {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Your Level *</label>
                <select className="form-select" value={meta.level} onChange={e => setMeta({ ...meta, level: e.target.value })}>
                  <option value="">Select your level</option>
                  {LEVELS.map(l => <option key={l.v} value={l.v}>{l.l}</option>)}
                </select>
              </div>
            </div>
            <div style={{ padding:'10px 14px', background:'rgba(201,151,58,0.04)', borderRadius:8, border:'1px solid rgba(0,212,255,0.12)', marginTop:8 }}>
              <div style={{ fontSize:12, color:'var(--text2)', lineHeight:1.6 }}>
                <strong style={{ color:'var(--blue)' }}>Rate honestly.</strong> There are no right or wrong answers. Your individual response is never shown — only aggregate patterns across all respondents matter.
                <br /><strong>This assessment covers {dims.length} dimensions × 10 questions = {dims.length * 10} questions total.</strong> Takes 15–25 minutes.
              </div>
            </div>
          </div>
        )}

        {/* STEP 1–N: Dimension questions */}
        {step >= 1 && step <= dims.length && currentDim && (() => {
          const [dimKey, dimData] = currentDim;
          const qs = getQsForDim(dimKey, dimData);
          const answered = qs.filter(q => answers[q.key]).length;
          return (
            <div className="card">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, padding:'8px 12px', background:'var(--bg3)', borderRadius:8 }}>
                <span style={{ fontSize:12, color:'var(--text2)' }}>Dimension {dimsDone + 1} of {dims.length}</span>
                <span style={{ fontFamily:'var(--ff-m)', fontSize:12, color: answered === qs.length ? 'var(--green)' : 'var(--text3)' }}>
                  {answered}/{qs.length} answered
                </span>
              </div>
              {qs.map((q, i) => (
                <div key={q.key} style={{ marginBottom:22, paddingBottom:22, borderBottom: i < qs.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div className="q-num">Q{(info.dimOrder?.indexOf(dimKey) || 0) * 10 + i + 1}</div>
                  <div className="q-text">{q.text}</div>
                  <div className="scale-btns">
                    {[1, 2, 3, 4, 5].map(v => (
                      <button key={v} className={`scale-btn ${answers[q.key] === v ? 'sel' : ''}`}
                        style={{ borderColor: answers[q.key] === v ? SCALE_COLORS[v] : '' }}
                        onClick={() => setAnswers({ ...answers, [q.key]: v })}>
                        {v}
                        <div className="scale-lbl">{v === 1 ? 'Strongly\nDisagree' : v === 2 ? 'Disagree' : v === 3 ? 'Neutral' : v === 4 ? 'Agree' : 'Strongly\nAgree'}</div>
                      </button>
                    ))}
                  </div>
                  {answers[q.key] && (
                    <div style={{ fontSize:11, color:SCALE_COLORS[answers[q.key]], marginTop:5, fontFamily:'var(--ff-m)' }}>
                      {SCALE_LABELS[answers[q.key]]}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })()}

        {/* Open-ended */}
        {isOpenEnded && (
          <div className="card">
            <div style={{ padding:'10px 14px', background:'rgba(245,166,35,0.05)', borderRadius:8, border:'1px solid rgba(245,166,35,0.2)', marginBottom:18 }}>
              <div style={{ fontSize:12, color:'var(--text2)' }}>
                These are <strong style={{ color:'var(--blue)' }}>optional</strong> but generate the richest insights. Be specific — "communication is poor" is less useful than "decisions made by leadership take 2–3 weeks to reach my team."
              </div>
            </div>
            {(info.openEnded || []).map((q, i) => (
              <div key={i} style={{ marginBottom:18 }}>
                <div className="q-num">Open Q{i + 1}</div>
                <div className="q-text">{q}</div>
                <textarea className="form-textarea" style={{ minHeight:72 }} placeholder="Be honest and specific..."
                  value={openAnswers[i]} onChange={e => { const a = [...openAnswers]; a[i] = e.target.value; setOpenAnswers(a); }} />
              </div>
            ))}
          </div>
        )}

        {error && <div className="form-error" style={{ margin:'12px 0' }}>{error}</div>}

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:20 }}>
          <button className="btn btn-ghost" style={{ visibility: step === 0 ? 'hidden' : 'visible' }} onClick={() => { setStep(s => Math.max(0, s - 1)); setTimeout(scrollToTop, 50); }}>← Back</button>
          {step < totalSections - 1
            ? <button className="btn btn-primary" style={{ padding:'10px 24px' }} disabled={!canProceed()} onClick={() => { setStep(s => s + 1); setTimeout(scrollToTop, 50); }}>Continue →</button>
            : <button className="btn btn-green" style={{ padding:'11px 28px' }} disabled={submitting || !canProceed()} onClick={submit}>
                {submitting ? 'Submitting…' : '✓ Submit Assessment'}
              </button>
          }
        </div>

        <div style={{ textAlign:'center', marginTop:24, fontSize:11, color:'var(--text3)' }}>
          Powered by Enpower Org DNA™ · Your data is secure and aggregated
        </div>
      </div>
    </div>
  );
}
